<template>
  <div class="educational-material-list">
    <wtr-educational-material-list
        :list="educationalMaterials"
        :options="{
          totalElements: totalMaterials,
          pagination: true,
          menuToggle: true,
        }"
        :params="paginationParams"
        :showLoader="loading"
        :tumorSites="tumorSites"
        :currentFolder="currentFolder"
        :subFilters="subFilters"
        :configSubFilters="configSubFilters"
        @filter-change="handleFilterChange"
        @create-new="$emit('open-type-selector')"
        @grid-mode="handleGridModeChange"
        @size-change="handleSizeChange"
        @page-change="handlePageChange"
      >
        <!-- List View (Table) -->
        <template v-slot:list-table>
          <b-table
            thead-class="bg-primary text-light"
            primary-key="id"
            outlined
            responsive
            hover
            :fields="tableFields"
            :items="educationalMaterials"
            show-empty
            empty-text="No educational materials found"
            :busy="loading"
            :sort-by.sync="paginationParams.sort"
            :sort-desc.sync="paginationParams.sortDesc"
          >
            <!-- Headers -->
            <template v-slot:head(type)="data">
              <div class="d-inline-flex align-items-center text-nowrap">
                <div class="text-nowrap">{{ data.label }}</div>
                <span
                  class="cursor-pointer"
                  v-on:click="handleSort('slide_collection_type')"
                >
                  &nbsp;<i class="fa fa-sort ml-1" aria-hidden="true"></i>
                </span>
              </div>
            </template>
            <template v-slot:head(idNumber)="data">
              <div class="d-inline-flex align-items-center text-nowrap">
                <div class="text-nowrap">{{ data.label }}</div>
                <span
                  class="cursor-pointer"
                  v-on:click="handleSort('system_id_number')"
                >
                  &nbsp;<i class="fa fa-sort ml-1" aria-hidden="true"></i>
                </span>
              </div>
            </template>
            <template v-slot:head(tumorSite)="data">
              <div class="text-nowrap">{{ data.label }}</div>
            </template>
            <template v-slot:head(title)="data">
              <div class="d-inline-flex align-items-center text-nowrap">
                <div class="text-nowrap">{{ data.label }}</div>
                <span class="cursor-pointer" v-on:click="handleSort('title')">
                  &nbsp;<i class="fa fa-sort ml-1" aria-hidden="true"></i>
                </span>
              </div>
            </template>
            <template v-slot:head(difficultyLevel)="data">
              <div class="d-inline-flex align-items-center text-nowrap">
                <div class="text-nowrap">{{ data.label }}</div>
                <span
                  class="cursor-pointer"
                  v-on:click="handleSort('difficulty_level')"
                >
                  &nbsp;<i class="fa fa-sort ml-1" aria-hidden="true"></i>
                </span>
              </div>
            </template>
            <template v-slot:head(createdDateTime)="data">
              <div class="d-inline-flex align-items-center text-nowrap">
                <div class="text-nowrap">{{ data.label }}</div>

                <span
                  class="cursor-pointer"
                  v-on:click="handleSort('createdDate')"
                >
                  &nbsp;<i class="fa fa-sort ml-1" aria-hidden="true"></i>
                </span>
              </div>
            </template>
            <template v-slot:head(authors)="data">
              <div class="d-inline-flex align-items-center text-nowrap">
                <div class="text-nowrap">{{ data.label }}</div>
                <span
                  class="cursor-pointer"
                  v-on:click="handleSort('u.first_name')"
                >
                  &nbsp;<i class="fa fa-sort ml-1" aria-hidden="true"></i>
                </span>
              </div>
            </template>
            <template v-slot:head(actions)="data">
              <div class="text-nowrap">{{ data.label }}</div>
            </template>

            <!-- Cells -->
            <template v-slot:cell(type)="row">
              <div class="fs-13">
                {{
                  row.item.type === "Assignment"
                    ? "Unknown Slides Assignment"
                    : row.item.type === "Collection" &&
                      row.item.slideCollectionType
                    ? row.item.slideCollectionType === "VIEW_HIDE_DIAGNOSIS"
                      ? "Case Collection: View/Hide Diagnosis"
                      : `Case Collection: Diagnosis Shown`
                    : row.item.type === "Presentation" &&
                      row.item.slideCollectionType
                    ? row.item.slideCollectionType === "VIEW_HIDE_DIAGNOSIS"
                      ? "Presentation: View/Hide Diagnosis"
                      : `Presentation: Diagnosis Shown`
                    : row.item.type
                }}
              </div>
            </template>
            <template v-slot:cell(idNumber)="row">
              <div class="fs-13">
                {{ row.item.systemIdNumber }}
              </div>
            </template>
            <template v-slot:cell(tumorSite)="row">
              <div class="fs-13">
                {{ row.item.tumorSite || "N/A" }}
              </div>
            </template>
            <template v-slot:cell(title)="row">
              <div class="fs-13">
                {{ row.item.title }}
              </div>
            </template>
            <template v-slot:cell(difficultyLevel)="row">
              <div class="fs-13">
                {{ row.item.difficultyLevel.replace(/_/g, " ") }}
              </div>
            </template>
            <template v-slot:cell(createdDateTime)="row">
              <div class="fs-13">
                {{ new Date(row.item.createdDate).toLocaleDateString() }}
              </div>
            </template>
            <template v-slot:cell(authors)="row">
              <div class="fs-13">
                {{ row.item.author.replace(/_/g, " ") }}
              </div>
            </template>
            <template v-slot:cell(actions)="row">
              <table-actions
                :key="row.item.id"
                :material="row.item"
                :copyIcon="
                  currentFolder &&
                  (currentFolder.folderType === 'EDUCATIONALOTHER' ||
                    currentFolder.folderType === 'EDUCATIONAL')
                    ? { show: 'Copy' }
                    : { show: false }
                "
                :viewIcon="{ show: 'View' }"
                :shareIcon="
                  currentFolder &&
                  (currentFolder.folderType === 'EDUCATIONALPUBLIC' || currentFolder.folderType === 'EDUCATIONALOTHER' ||
                    currentFolder.folderType === 'EDUCATIONAL')
                    ? { show: 'Share' }
                    : { show: false }
                "
                :editIcon="
                  currentFolder &&
                  (currentFolder.folderType === 'EDUCATIONALOTHER' ||
                    currentFolder.folderType === 'EDUCATIONAL')
                    ? { show: 'Edit' }
                    : { show: false }
                "
                :deleteIcon="
                  currentFolder &&
                  (currentFolder.folderType === 'EDUCATIONALOTHER' ||
                    currentFolder.folderType === 'EDUCATIONAL')
                    ? { show: 'Delete' }
                    : { show: false }
                "
                :caseInformationIcon="{ show: 'Show Description' }"
                :moveToSlideLibraryIcon="
                  getPublicationAction(row.item)
                "
                :moveBackForRevisionIcon="
                  currentFolder &&
                  (currentFolder.folderType === 'EDUCATIONALPUBLIC') && (canDirectlyPublish)
                    ? { show: true, title: 'Move back to Private' }
                    : { show: false }
                "
                @view="viewMaterialInfo(row.item)"
                @edit="$emit('edit-item', row.item)"
                @delete="deleteItem(row.item)"
                @moveBackForRevision="moveBacktoUserFolder(row.item)"
                @caseInformation="showCaseDescription(row.item)"
                @moveToSlideLibrary="handlePublicationAction(row.item)"
                @share="(event) => onShareButtonClick(event, row.item)"
                @copyIcon="copyMaterial(row.item)"
              ></table-actions>
            </template>
          </b-table>
        </template>
        <template v-slot:empty>
          <tr>
            <td :colspan="tableFields.length" class="text-center py-5">
              <div class="text-muted">
                <i class="fas fa-inbox fa-2x mb-2"></i>
                <h5>No educational materials found</h5>
                <p class="mb-0">
                  Try adjusting your filters or creating a new item
                </p>
              </div>
            </td>
          </tr>
        </template>
      </wtr-educational-material-list>

    <!-- Description Modal -->
    <b-modal id="show-description-modal" title="Description" hide-footer>
      <div v-if="selectedRow" class="d-flex flex-column align-items-center">
        <p class="mb-3">
          {{ selectedRow.description || "No description available" }}
        </p>
      </div>
    </b-modal>
  </div>
</template>

<script>
import TableActionsComponent from "../../../../components/common/table-actions.vue";
import WTREducationalMaterialListComponent from "../../../../components/common/wtr-educational-material-list.vue";
import {
  ROLE_CHAIRPERSON,
  ROLE_CHIEF_EDITOR,
  ROLE_CONTRIBUTOR,
  ROLE_EDITOR,
  ROLE_GUEST,
  ROLE_GUEST_WTR_MATERIAL,
  ROLE_PROJECT_ADMIN,
  ROLE_TENANT_ADMIN,
} from "../../../../constants/constants-role";
import {
  buildCopiedMaterialItem,
  generateCopiedMaterialId,
} from "../../utils/copy-material";
import { buildEducationalMaterialShortLinkData } from "../../utils/material-short-link";

export default {
  name: "EducationalMaterialList",
  components: {
    TableActions: TableActionsComponent,
    "wtr-educational-material-list": WTREducationalMaterialListComponent,
  },
  props: {
    currentFolder: {
      type: Object,
      default: null,
    },
    tumorSites: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      educationalMaterials: [],
      paginationParams: {
        page: 0,
        size: 10,
        sort: "createdDate,desc",
        sortDesc: true,
      },
      totalMaterials: 0,
      loading: false,
      gridMode: false,
      currentFilters: {
        search: "",
        slideCollectionType: "",
        level: "",
        tumorSiteId: null,
        isRecommended: null,
      },
      selectedRow: null,
      configSubFilters: [
        {
          optionLabel: "name",
          optionValue: "id",
          placeholder: "Learning Modality",
          searchParam: "slideCollectionType",
        },
        {
          optionLabel: "name",
          optionValue: "id",
          placeholder: "Tumor Site",
          searchParam: "tumorSiteId",
        },
        {
          optionLabel: "name",
          optionValue: "id",
          placeholder: "Difficulty Level",
          searchParam: "level",
        },
        // {
        //   optionLabel: "name",
        //   optionValue: "id",
        //   placeholder: "Recommended By",
        //   searchParam: "isRecommended",
        // },
      ],
      subFilters: [[], [], []],
      indexTumorSiteId: 1,
      indexDifficultyLevel: 2,
      indexLearningModality: 0,
      indexRecommendedBy: 3,
    };
  },
  computed: {
    tableFields() {
      return [
        { key: "type", label: this.$t("Learning Modality") },
        { key: "idNumber", label: this.$t("ID") },
        { key: "tumorSite", label: this.$t("Tumor Site") },
        { key: "title", label: this.$t("Title") },
        { key: "difficultyLevel", label: this.$t("Difficulty Level") },
        { key: "createdDateTime", label: this.$t("Date of Creation") },
        { key: "authors", label: this.$t("Authors") },
        { key: "actions", label: this.$t("Actions") },
      ];
    },
    staticDifficultyLevels() {
      return [
        { id: "Junior_Residents", name: "Junior Residents" },
        { id: "Pathology_Fellows", name: "Pathology Fellows" },
        { id: "Practicing_Pathologist", name: "Practicing Pathologist" },
        { id: "Mixed", name: "Mixed" },
        { id: "NA", name: "NA" },
      ];
    },
    staticLearningModalities() {
      return [
        { name: "All", id: "all" },
        { name: "Case Collection: Diagnosis Shown", id: "DIAGNOSIS_SHOWN" },
        {
          name: "Case Collection: View/Hide Diagnosis",
          id: "VIEW_HIDE_DIAGNOSIS",
        },
        { name: "Unknown Slides Assignment", id: "UNKNOWN_SLIDES_ASSIGNMENT" },
      ];
    },
    canManagePublication() {
      const userRole = localStorage.getItem("userRole");
      return [
        ROLE_TENANT_ADMIN,
        ROLE_PROJECT_ADMIN,
        ROLE_CHAIRPERSON,
      ].includes(userRole);
    },
    isGuestWtrMaterialUser() {
      return localStorage.getItem("userRole") === ROLE_GUEST_WTR_MATERIAL;
    },
    canDirectlyPublish() {
      return this.canManagePublication || this.isGuestWtrMaterialUser;
    },
    canRequestPublication() {
      const userRole = localStorage.getItem("userRole");
      return [
        ROLE_GUEST,
        ROLE_CONTRIBUTOR,
        ROLE_EDITOR,
        ROLE_CHIEF_EDITOR,
      ].includes(userRole);
    },
  },
  watch: {
    tumorSites: {
      handler(newTumorSites) {
        if (newTumorSites && newTumorSites.length > 0) {
          this.subFilters[this.indexTumorSiteId] = [...newTumorSites];
        }
      },
      immediate: true,
      deep: true,
    },
  },
  mounted() {
    this.initializeFilters();
    if (this.currentFolder) {
      this.fetchFolderMaterials();
    }
  },
  methods: {
    initializeFilters() {
      this.subFilters[this.indexDifficultyLevel] = this.staticDifficultyLevels;
      this.subFilters[this.indexLearningModality] =
        this.staticLearningModalities;
      // this.subFilters[this.indexRecommendedBy] = this.staticRecommendedBy;
    },

    sleep(ms) {
      return new Promise((resolve) => setTimeout(resolve, ms));
    },

    getAccessibleTumorSiteIds() {
      const userRole = localStorage.getItem("userRole")?.toUpperCase();
      return userRole === ROLE_TENANT_ADMIN
        ? localStorage.getItem("tumorSiteIdsString") || null
        : localStorage.getItem("tumorSiteId") || null;
    },

    handleSort(sortField) {
      const currentSortField = this.paginationParams.sort.split(",")[0];

      if (currentSortField === sortField) {
        this.paginationParams.sortDesc = !this.paginationParams.sortDesc;
      } else {
        this.paginationParams.sortDesc = false;
      }

      const direction = this.paginationParams.sortDesc ? "desc" : "asc";
      this.paginationParams.sort = `${sortField},${direction}`;
      this.paginationParams.page = 0;

      this.fetchFolderMaterials();
    },

    async fetchFolderMaterials() {
      await this.sleep(100);
    //   if (this.initialLoading) return;

      this.loading = true;
      try {
        const queryParams = {
          page: this.paginationParams.page,
          size: this.paginationParams.size,
          sort: this.paginationParams.sort,
          sortDirection: this.paginationParams.sortDesc ? "desc" : "asc",
        };
        const tumorSiteIdsList =
          this.currentFilters.tumorSiteId || this.getAccessibleTumorSiteIds();
        const body = {
          userId: this.currentFolder.userId || localStorage.getItem("userId"),
          isPublished: this.currentFolder.folderType === "EDUCATIONALPUBLIC" ? 1 : 0,
          ...this.currentFilters,
          tumorSiteIdsList,
        };

        Object.keys(body).forEach((key) => {
          if (
            body[key] === null ||
            body[key] === "" ||
            body[key] === undefined
          ) {
            delete body[key];
          }
        });

        const response = await this.$api.getMaterialByFolder(queryParams, body);
        if (this.$handleResponse(response)) {
          const payload = response.data.payload;
          this.educationalMaterials = payload?.content || [];
          this.totalMaterials =
            payload?.totalElements || this.educationalMaterials.length;
          this.$emit("folder-changed");
        }
      } catch (error) {
        this.$handleError(error);
        this.educationalMaterials = [];
        this.totalMaterials = 0;
      } finally {
        this.loading = false;
        // this.initialLoading = false;
      }
    },

    handleGridModeChange(mode) {
      this.gridMode = mode;
    },

    handleSizeChange(params) {
      this.paginationParams.size = params.size;
      this.paginationParams.page = 0;
      this.fetchFolderMaterials();
    },

    handlePageChange(params) {
      this.paginationParams.page = params.page;
      this.fetchFolderMaterials();
    },

    handleFilterChange(newFilters) {
      if (Object.keys(newFilters).length === 0) {
        this.currentFilters = {
          search: "",
          slideCollectionType: "",
          level: "",
          tumorSiteId: null,
          isRecommended: null,
        };
      } else {
        this.currentFilters = {
          ...this.currentFilters,
          ...newFilters,
        };
      }

      this.paginationParams.page = 0;
      this.fetchFolderMaterials();
    },

    async viewMaterialInfo(eduMaterial) {
      console.log("Viewing material:", eduMaterial);
      const isPublicSite = this.currentFolder.folderType === "EDUCATIONALPUBLIC";
      const route = "edu-case-viewer";
      this.$changeRouteToTab({
        name: route,
        params: {
          id: eduMaterial.id,
          isPublicSite,
          tumorSiteId: localStorage.getItem("tumorSiteId") || 1,
          tumorSiteName: eduMaterial.tumorSite,
          type: eduMaterial.type,
          backToPublicSite: false,
          activeTab: "none",
          isMaterialPublished: eduMaterial.isShowPublicSite,
        },
      });
    },
    editItem(item) {
      this.selectedEditId = item.id;
      this.selectedType = item.type;
      this.selectedUserId = item.author;
      this.$bvModal.show("create-material-modal");
    },

    showCaseDescription(item) {
      this.selectedRow = item;
      this.$bvModal.show("show-description-modal");
    },

    getPublicationAction(item) {
      const isPrivateFolder =
        this.currentFolder &&
        (this.currentFolder.folderType === "EDUCATIONALOTHER" ||
          this.currentFolder.folderType === "EDUCATIONAL");

      if (!isPrivateFolder) return { show: false };

      if (this.canDirectlyPublish) {
        return {
          show: true,
          title:
            this.canManagePublication &&
            item.publicationApprovalStatus === "PENDING"
              ? "Approve publishing"
              : "Move to Public",
        };
      }

      return this.canRequestPublication && !item.isPublishedByOther
        ? { show: true, title: "Move to Public" }
        : { show: false };
    },

    handlePublicationAction(item) {
      if (this.canDirectlyPublish) {
        this.approvePublication(item);
        return;
      }
      this.moveToPublicbox(item);
    },

    async moveToPublicbox(item) {
      this.$deleteConfirmation(
        "Materials must be approved by Administrator before going to Public WTR website. Do you want to submit request for approval?",
        async () => {
          try {
            const encryptedData = buildEducationalMaterialShortLinkData(
              item,
              this.$encryptData,
              {
                isPublicSite: false,
                tumorSiteId:
                  item.tumorSiteId || localStorage.getItem("tumorSiteId") || 1,
              }
            );
            const shortLinkResponse = await this.$api.postCaseUrl({
              encryptedData,
            });
            if (!shortLinkResponse?.data?.id) {
              throw new Error("Failed to generate the material review link");
            }

            const payload = {
              ids: [item.id],
              materialLink: `${window.location.origin}/edu-short-links/${shortLinkResponse.data.id}`,
            };

            const response = await this.$api.moveToPublicbox(payload);
            const resData = response.data || response;
            if (resData.status == true) {
              this.$toastr(
                "success",
                "Publication approval request submitted successfully"
              );
              this.fetchFolderMaterials();
            } else {
              throw new Error("Failed to submit publication approval request");
            }
          } catch (error) {
            this.$handleError(error);
          }
        },
        undefined,
        { cancel: "No", confirm: "Yes" }
      );
    },

    async approvePublication(item) {
      const isPendingApproval =
        this.canManagePublication &&
        item.publicationApprovalStatus === "PENDING";
      this.$deleteConfirmation(
        isPendingApproval
          ? `Are you sure you want to approve publishing material "${item.title}"?`
          : `Are you sure you want to move material "${item.title}" to Public?`,
        async () => {
          try {
            const response = await this.$api.approveMaterialPublication({
              ids: [item.id],
            });
            const resData = response.data || response;
            if (resData.status == true) {
              if (item.systemIdNumber == item.idNumber) {
                this.$emit("move-to-public", {
                  systemIdNumber: resData.payload,
                  creationId: item.idNumber,
                });
              }
              this.$toastr(
                "success",
                isPendingApproval
                  ? "Material approved and moved to the Public Access Box"
                  : "Material moved to the Public Access Box"
              );
              this.fetchFolderMaterials();
            } else {
              throw new Error("Failed to approve material publication");
            }
          } catch (error) {
            this.$handleError(error);
          }
        },
        undefined,
        { cancel: "No", confirm: "Yes" }
      );
    },

    async moveBacktoUserFolder(item) {
      this.$deleteConfirmation(
        `Are you sure you want to move material "${item.title}" to private folder?`,
        async () => {
          try {
            const payload = {
              ids: [item.id],
            };

            const response = await this.$api.moveToUserFolder(payload);
            const resData = response.data || response;

            if (resData.status == true) {
              this.$toastr(
                "success",
                "Material moved back to user folder successfully"
              );
              this.fetchFolderMaterials();
            } else {
              throw new Error("Failed to move material");
            }
          } catch (error) {
            this.$handleError(error);
          }
        }
      );
    },

    deleteItem(item) {
      this.$deleteConfirmation(
        `Are you sure you want to delete "${item.title}"?`,
        async () => {
          try {
            const response = await this.$api.deleteEducationalMaterial(item.id);
            if (response.data.statusMessage !== "Success") {
              throw new Error("Failed to delete material");
            }
            this.$toastr("success", "Material deleted successfully");
            this.fetchFolderMaterials();
          } catch (error) {
            this.$handleError(error);
          }
        }
      );
    },

    onShareButtonClick(event, material) {
      this.$emit("share-button-click", event, material );
    },
    async getExistingCopyIds(sourceId) {
      const pageSize = 100;
      const materialIds = [];
      let page = 0;
      let totalPages = 1;

      do {
        const queryParams = {
          page,
          size: pageSize,
          sort: "createdDate,desc",
          sortDirection: "desc",
        };
        const body = {
          userId: this.currentFolder.userId || localStorage.getItem("userId"),
          isPublished: 0,
          search: `COPY-${sourceId}-`,
          tumorSiteIdsList: this.getAccessibleTumorSiteIds(),
        };

        if (!body.tumorSiteIdsList) {
          delete body.tumorSiteIdsList;
        }

        const response = await this.$api.getMaterialByFolder(queryParams, body);
        if (!response?.data?.status) {
          throw new Error(
            response?.data?.statusMessage || "Unable to generate the copy ID"
          );
        }

        const payload = response.data.payload || {};
        const materials = payload.content || [];
        materials.forEach((existingMaterial) => {
          materialIds.push(
            existingMaterial.idNumber,
            existingMaterial.systemIdNumber
          );
        });

        totalPages =
          payload.totalPages ||
          Math.max(1, Math.ceil((payload.totalElements || 0) / pageSize));
        page += 1;
      } while (page < totalPages);

      return materialIds.filter(Boolean);
    },
    async copyMaterial(material) {
      if (this.loading) return;

      this.loading = true;
      try {
        const response = await this.$api.getEducationalMaterialById(
          material.id
        );
        const eduMaterial = response.data.payload.eduMaterial;
        const caseDetails = response.data.payload.caseDetails || [];
        const existingCopyIds = await this.getExistingCopyIds(
          eduMaterial.idNumber
        );

        const newEduMaterial = {
          type: eduMaterial.type,
          idNumber: generateCopiedMaterialId(
            eduMaterial.idNumber,
            existingCopyIds
          ),
          title: eduMaterial.title,
          description: eduMaterial.description || "",
          difficultyLevel: eduMaterial.difficultyLevel,
          slideCollectionType: eduMaterial.slideCollectionType || null,
          isHideCaseNotes: eduMaterial.isHideCaseNotes ? 1 : 0,
          isHidePatientInfo: eduMaterial.isHidePatientInfo ? 1 : 0,
          isDiagnosisUnhide: eduMaterial.isDiagnosisUnhide ? 1 : 0,
          isRecommendedWtr: eduMaterial.isRecommendedWtr ? 1 : 0,
          isShowPublicSiteEditor: eduMaterial.isShowPublicSiteEditor ? 1 : 0,
          isShowPublicSite: 0,
          createdBy: Number(
            this.currentFolder?.userId || localStorage.getItem("userId")
          ),
          tumorSite: eduMaterial.tumorSite
            ? {
                id: Number(eduMaterial.tumorSite.id),
                name: eduMaterial.tumorSite.name,
              }
            : null,
        };

        const newEduCases = caseDetails.map(buildCopiedMaterialItem);
        const payload = {
          eduMaterial: newEduMaterial,
          eduCases: newEduCases,
        };

        const createResp = await this.$api.addEducationalMaterial(payload);
        if (this.$handleResponse && !this.$handleResponse(createResp)) {
          return;
        }
        this.$toastr("success", "Material copied successfully");
        await this.fetchFolderMaterials();
      } catch (error) {
        this.$handleError ? this.$handleError(error) : console.error(error);
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.fs-13 {
  font-size: 13px !important;
}
.fs-13.fa-copy {
  font-size: 13px !important;
}
</style>
